<template>
  <div class="container">
    <a-button
      type="primary"
      @click="openAdd = true"
      style="margin-bottom: 16px"
    >
      添加轮播图
    </a-button>
    <a-table
      :columns="columns"
      :data-source="carouselList"
      :pagination="{
        current: listRequest.current,
        pageSize: listRequest.size,
        total: total,
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 30, 40, 50],
        onChange: handleChange,
        onShowSizeChange: handleSizeChange,
      }"
      bordered
    >
      <template
        #bodyCell="{ column, record }: { column: ColumnType, record: Carousel }"
      >
        <template v-if="column.dataIndex === 'category'">
          <span v-if="record.category === 1">首页</span>
          <span v-if="record.category === 2">商城</span>
        </template>
        <template v-else-if="column.dataIndex === 'image_url'">
          <a-image
            :src="ToImage(record.image_url)"
            style="height: 60px; width: auto"
          />
        </template>
        <template v-else-if="column.dataIndex === 'is_delete'">
          <a-tag v-if="record.is_delete === 1" color="green">正常</a-tag>
          <a-tag v-if="record.is_delete === 2" color="red">回收站</a-tag>
        </template>
        <template v-else-if="column.dataIndex === 'create_at'">
          <span
            >{{ dayjs(record.create_at).format("YYYY-MM-DD HH:mm:ss") }}
          </span>
        </template>
        <template v-else-if="column.dataIndex === 'operator'">
          <a-button
            @click="handleUpdate(record)"
            type="primary"
            :style="{ marginRight: '8px' }"
            >编辑
          </a-button>
          <a-button
            @click="submitDelete(record)"
            type="primary"
            :style="{
              backgroundColor: '#ff7a45',
              color: 'white',
              marginRight: '8px',
            }"
            >删除
          </a-button>
          <a-button
            @click="submitDrop(record)"
            type="primary"
            :style="{
              backgroundColor: '#ff4d4f',
              color: 'white',
            }"
            >彻底删除
          </a-button>
        </template>
      </template>
    </a-table>
    <a-modal
      v-model:open="openAdd"
      title="添加轮播图数据"
      @ok="submitAdd"
      cancelText="取消"
    >
      <a-form :model="addRequest">
        <a-form-item label="轮播图名称">
          <a-input v-model:value="addRequest.name" />
        </a-form-item>
        <a-form-item label="优先级">
          <a-input-number v-model:value="addRequest.level" />
        </a-form-item>
        <a-form-item label="类别">
          <a-select v-model:value="addRequest.category">
            <a-select-option :value="1">首页</a-select-option>
            <a-select-option :value="2">商城</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="图片">
          <File-Upload v-model="addRequest.image_url" style="display: block" />
          <a-image
            v-if="addRequest.image_url !== ''"
            :src="ToImage(addRequest.image_url)"
            style="max-height: 150px; display: block; margin-top: 10px"
          />
        </a-form-item>
      </a-form>
    </a-modal>
    <a-modal
      v-model:open="openUpdate"
      title="修改轮播图数据"
      @ok="submitUpdate"
      cancelText="取消"
    >
      <a-form :model="updateRequest" style="margin-top: 20px">
        <a-form-item label="轮播图名称">
          <a-input v-model:value="updateRequest.name" />
        </a-form-item>
        <a-form-item label="优先级">
          <a-input-number v-model:value="updateRequest.level" />
        </a-form-item>
        <a-form-item label="类别">
          <a-select v-model:value="updateRequest.category">
            <a-select-option :value="1">首页</a-select-option>
            <a-select-option :value="2">商城</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="状态">
          <a-select v-model:value="updateRequest.is_delete">
            <a-select-option :value="1">正常</a-select-option>
            <a-select-option :value="2">回收站</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="图片">
          <File-Upload
            v-model="updateRequest.image_url"
            style="display: block"
          />
          <a-image
            :src="ToImage(updateRequest.image_url)"
            style="max-height: 150px; display: block; margin-top: 10px"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";
import { CarouselService } from "@/api/services/CarouselService";
import { ListRequest } from "@/api/models/ListRequest";
import { Carousel } from "@/api/models/carousel/Carousel";
import { message } from "ant-design-vue";
import { ToImage } from "@/utils/image";
import dayjs from "dayjs";
import { ColumnType } from "ant-design-vue/lib/table";
import { CarouselUpdateRequest } from "@/api/models/carousel/CarouselUpdateRequest";
import { CarouselAddRequest } from "@/api/models/carousel/CarouselAddRequest";
import FileUpload from "@/components/FileUpload.vue";

const total = ref<number>(0);

const listRequest = reactive<ListRequest>({
  current: 1,
  size: 20,
  content: "",
});

const carouselList = ref<Carousel[]>([]);

const handleList = async () => {
  const res = await CarouselService.ListAll(listRequest);
  if (res.code === 0) {
    carouselList.value = res.data.records;
    total.value = res.data.total;
  } else {
    message.error(res.msg);
  }
};

onMounted(async () => {
  await handleList();
});

const handleSizeChange = (current: number, pageSize: number) => {
  listRequest.current = current;
  listRequest.size = pageSize;
  handleList();
};

const handleChange = (page: number, pageSize: number) => {
  listRequest.current = page;
  listRequest.size = pageSize;
  handleList();
};

const openAdd = ref(false);

const addRequest = reactive<CarouselAddRequest>({
  name: "",
  image_url: "",
  level: 1,
  category: 1,
});

const submitAdd = async () => {
  const res = await CarouselService.Add(addRequest);
  if (res.code === 0) {
    message.success("添加成功");
    setTimeout(() => location.reload(), 1000);
  } else {
    message.error(res.msg);
  }
};

const openUpdate = ref(false);

const updateRequest = reactive<CarouselUpdateRequest>({
  id: 0,
  name: "",
  image_url: "",
  level: 0,
  category: 0,
  is_delete: 0,
});

const handleUpdate = (record: Carousel) => {
  openUpdate.value = true;
  Object.assign(updateRequest, record);
};

const submitUpdate = async () => {
  const res = await CarouselService.Update(updateRequest);
  if (res.code === 0) {
    message.success("修改成功");
    setTimeout(() => location.reload(), 1000);
  } else {
    message.error(res.msg);
  }
};

const submitDelete = async (record: Carousel) => {
  const res = await CarouselService.Delete(record.id);
  if (res.code === 0) {
    message.success("删除成功");
    setTimeout(() => location.reload(), 1000);
  } else {
    message.error(res.msg);
  }
};

const submitDrop = async (record: Carousel) => {
  const res = await CarouselService.Drop(record.id);
  if (res.code === 0) {
    message.success("删除成功");
    setTimeout(() => location.reload(), 1000);
  } else {
    message.error(res.msg);
  }
};

const columns: ColumnType[] = [
  {
    title: "Id",
    dataIndex: "id",
    align: "center",
  },
  {
    title: "轮播名称",
    dataIndex: "name",
    align: "center",
  },
  {
    title: "优先级",
    dataIndex: "level",
    align: "center",
  },
  {
    title: "轮播位置",
    dataIndex: "category",
    align: "center",
  },
  {
    title: "图片",
    dataIndex: "image_url",
    align: "center",
  },
  {
    title: "状态",
    dataIndex: "is_delete",
    align: "center",
  },
  {
    title: "创建时间",
    dataIndex: "create_at",
    align: "center",
  },
  {
    title: "操作",
    dataIndex: "operator",
    align: "center",
  },
];
</script>

<style scoped>
.container {
  border: 1px solid #d9d9d9;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 98%;
  margin-left: 1%;
  background-color: white;
  margin-top: 16px;
}
</style>
